import React, { useCallback, useEffect, useState } from "react";
import PageContentContainer from "../../Container/PageContentContainer";
import _ from "lodash";
import ActivityOverviewHeader from "../ActivityOverviewHeader/ActivityOverviewHeader";
import ApiClient from "../../../services/ApiClient";
import { ACTIVITY_OVERDUE } from "../../../services/Activities/ActivityStatus";
import { buildApiFilters, buildSearchParams, prepareFilters } from "../../../services/Activities/activityFunctions";
import ActivityTable from "../ActivityTable/ActivityTable";
import useActivitiesFetching from "../../../hooks/useActivitiesFetching";
import { Paper, TableContainer } from "@mui/material";
import { deadlineActivityTypes } from "../../../services/Activities/activityTypes";
import PageHeadline from "../../PageHeadline/PageHeadline";
import { useBackofficeUser } from "../../../provider/BackofficeUserProvider";
import { useCurrentUser } from "../../../provider/CurrentUserProvider";
import { Activity } from "../../../types/Activity";
import { BackofficeUser } from "../../../types/BackofficeUser";
import dayjs from "dayjs";

const DeadlineOverview = () => {
  const { lawyers, backofficeUsers } = useBackofficeUser();
  const currentUser = useCurrentUser();

  const initialFilters = prepareFilters(currentUser, lawyers, "deadlineFilters", "allDeadlines");

  const [productFilter, setProductFilter] = useState(initialFilters.productFilter);
  const [activityTypeFilter, setActivityTypeFilter] = useState(initialFilters.activityTypeFilter);
  const [userFilter, setUserFilter] = useState(initialFilters.userFilter);
  const [timespanFilter, setTimespanFilter] = useState(initialFilters.timespanFilter);
  const [subjectFilter, setSubjectFilter] = useState(initialFilters.subjectFilter);

  const [userActivitiesToday, setUserActivitiesToday] = useState<Activity[]>([]);
  const [refreshCounter, setRefreshCounter] = useState(0);

  const dataSource = buildSearchParams(
    _.merge(
      buildApiFilters({
        productFilter: productFilter,
        activityTypeFilter: activityTypeFilter,
        userFilter: userFilter,
        timespanFilter: timespanFilter,
        subjectFilter: subjectFilter,
      }),
      {
        refreshCounter: refreshCounter,
      }
    )
  );

  let dataSourceString = dataSource.toString();

  if (userFilter === "all") {
    _.forEach(lawyers, (lawyer) => {
      dataSourceString += "&assignedUser[]=" + lawyer["@id"];
    });
  }

  const { activities, isLoading } = useActivitiesFetching(dataSourceString, backofficeUsers);

  const postProcessFilterActivities = useCallback(
    (activities: Activity<BackofficeUser>[]) => {
      if (timespanFilter === "overdue") {
        activities = _.filter(activities, ["activityStatus", ACTIVITY_OVERDUE]);
      }
      return activities;
    },
    [timespanFilter]
  );

  const updateUserActivitiesToday = useCallback(async () => {
    if (userFilter === "all") {
      // this is only used to show the number of activities, which is not needed if all users' activities are shown
      setUserActivitiesToday([]);
      return;
    }
    const filters = {
      assignedUser: userFilter,
      "dueDate[before]": dayjs().format("YYYY-MM-DD"),
      type: deadlineActivityTypes,
      done: false,
      deleted: false,
      pagination: false,
    };

    let searchParams = buildSearchParams(filters);

    const userActivitiesResult = await ApiClient.get("activities?" + searchParams.toString());
    setUserActivitiesToday(userActivitiesResult["hydra:member"]);
  }, [userFilter]);

  const updateActivities = () => {
    setRefreshCounter(refreshCounter + 1);
  };

  useEffect(() => {
    updateUserActivitiesToday();
  }, [userFilter, updateUserActivitiesToday]);

  useEffect(() => {
    localStorage.setItem(
      "deadlineFilters",
      JSON.stringify(
        _.merge(
          {},
          {
            productFilter: productFilter,
            activityTypeFilter: activityTypeFilter,
            userFilter: userFilter,
            timespanFilter: timespanFilter,
          }
        )
      )
    );
  }, [productFilter, userFilter, timespanFilter, activityTypeFilter]);

  const filteredActivities = isLoading ? [] : postProcessFilterActivities(activities!);

  if (lawyers.length < 1) {
    return null;
  }

  const overdueTotal = userActivitiesToday.reduce((total, activity) => {
    return activity.activityStatus === ACTIVITY_OVERDUE && !activity.done ? total + 1 : total;
  }, 0);

  return (
    <PageContentContainer>
      <PageHeadline main="Fristenkalender" />
      <ActivityOverviewHeader
        todayTotal={userActivitiesToday.length}
        overdueTotal={overdueTotal}
        setProductFilter={(data) => {
          updateActivities();
          setProductFilter(data);
        }}
        productFilter={productFilter}
        setActivityTypeFilter={(data) => {
          updateActivities();
          setActivityTypeFilter(data);
        }}
        activityTypeFilter={activityTypeFilter}
        userFilter={userFilter}
        setUserFilter={(data) => {
          updateActivities();
          setUserFilter(data);
        }}
        timespanFilter={timespanFilter}
        setTimespanFilter={(data) => {
          updateActivities();
          setTimespanFilter(data);
        }}
        setSubjectFilter={(data) => {
          updateActivities();
          setSubjectFilter(data);
        }}
        agents={lawyers}
        isDeadlineView
      />
      <TableContainer component={Paper}>
        <ActivityTable isLoading={isLoading} activities={filteredActivities} update={updateActivities} />
      </TableContainer>
    </PageContentContainer>
  );
};

export default DeadlineOverview;
